import React, { memo } from 'react';
import SelectCreatable from 'react-select/creatable';
import Select, {
  ActionMeta,
  components,
  MenuListProps,
  OptionProps,
} from 'react-select';
import styled from 'styled-components';
import Throbber from '../../../Throbber';

export type OptionTypeType = {
  value: string;
  label: string;
  type?: string;
  fullName?: string;
  email?: string;
  avatarUrl?: string | null;
  id?: string;
  __isNew__?: boolean;
};

export type ValueTypeType = {
  value: any;
  label?: string;

  avatarUrl?: string | null;
  clientId?: string;
  description?: string;
  disabled?: boolean;
  email?: string;
  fullName?: string;
  id?: string;
  isSegmentAllowed?: boolean;
  name?: string;
  phone?: string;
  skillGroupId?: string;
  surname?: string;
  type?: string;
};

type OptionAccessors = {
  getOptionLabel: (option: unknown) => string;
  getOptionValue: (option: unknown) => string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/;

// Keep spaces while searching by full name, but never include them in an email.
export const normalizeRecipientInput = (inputValue: string) =>
  inputValue.includes('@') ? inputValue.replace(/\s/g, '') : inputValue;

const isSameOption = (
  inputValue: string,
  option: unknown,
  accessors: OptionAccessors
) => {
  const normalizedInputValue = inputValue.toLowerCase();

  return [
    accessors.getOptionValue(option),
    accessors.getOptionLabel(option),
  ].some(
    optionValue => String(optionValue).toLowerCase() === normalizedInputValue
  );
};

export const isValidNewEmailOption = (
  inputValue: string,
  selectValue: readonly unknown[],
  selectOptions: readonly unknown[],
  accessors: OptionAccessors
) =>
  EMAIL_PATTERN.test(inputValue) &&
  !(selectValue || []).some(option =>
    isSameOption(inputValue, option, accessors)
  ) &&
  !(selectOptions || []).some(option =>
    isSameOption(inputValue, option, accessors)
  );

interface IMenuList extends MenuListProps {
  children: React.ReactNode;
}

export interface IUserSelect {
  options?: OptionTypeType[];
  value?: ValueTypeType | ValueTypeType[] | null;
  defaultValue?: ValueTypeType[];
  onChange?: (newValue: ValueTypeType[] | null) => void;
  onInputChange?: (inputValue: string) => void;
  isMulti?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isCreatable?: boolean;
  isBordered?: boolean;
}

interface IOption extends OptionProps {
  data: OptionTypeType;
}

const MenuList: React.FC<any> = (props: IMenuList) => {
  const { children } = props;
  return <components.MenuList {...props}>{children}</components.MenuList>;
};

const Option = (props: OptionProps<any, boolean>) => {
  const { data } = props;
  const { __isNew__, avatarUrl, fullName, type, label } = data;

  if (__isNew__) {
    return <components.Option {...props} />;
  }

  return (
    <components.Option {...props}>
      {avatarUrl && <StyledOptionAvatar src={avatarUrl} />}
      <StyledRow>
        <StyledOptionName>{fullName || 'Без имени'}</StyledOptionName>
        <StyledOptionDescription>
          {type === 'client' ? 'Клиент Level.Travel' : 'Менеджер Level.Travel'}
        </StyledOptionDescription>
      </StyledRow>
      <StyledOptionEmail>{label}</StyledOptionEmail>
    </components.Option>
  );
};

const LoadingMessage = props => (
  <StyledLoadingContainer {...props}>
    <StyledSpinner color="#007aff" />
  </StyledLoadingContainer>
);

const ContainerStyle = base => ({
  ...base,
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'static',
});

const MenuStyle = base => ({
  ...base,
  zIndex: 1000,
  padding: 0,
  margin: 0,
});

const MenuListStyle = base => ({
  ...base,
  width: '100%',
  maxHeight: '150px',
  padding: 0,
  margin: 0,
  whiteSpace: 'pre-line',
  color: '#17181a',
  borderRadius: '4px',
  backgroundColor: 'white',
  boxShadow:
    '0 10px 15px 0 rgba(196, 194, 182, 0.9), 0 1px 2px 0 rgba(210, 208, 197, 0.9)',
});

const OptionStyle = base => ({
  ...base,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#dcedff',
  },
});

const SingleValueStyle = (base, state) => ({
  ...base,
  backgroundColor: state.isDisabled ? '#e6e6e6' : 'white',
  border: '1px solid #C2C5CC',
  boxSizing: 'border-box',
  borderRadius: '40px',
  minWidth: 'auto',
  padding: '3px 6px',
  lineHeight: '16px',
  fontSize: '14px',
  color: '#4D4D4D',
});

const MultiValueStyle = (base, state) => ({
  ...base,
  backgroundColor: state.isDisabled ? '#e6e6e6' : 'white',
  border: '1px solid #C2C5CC',
  boxSizing: 'border-box',
  borderRadius: '40px',
  minWidth: 'auto',
});

const MultiValueLabelStyle = base => ({
  ...base,
  fontFamily: 'Backpack, sans-serif',
  fontSize: '14px',
  lineHeight: '16px',
  overflow: 'initial',
  textOverflow: 'unset',
  color: '#4D4D4D',
});

const MultiValueRemoveStyle = base => ({
  ...base,
  '&:hover': {
    background: 'transparent',
  },
  svg: {
    fill: '#919499',
    marginTop: '1px',
  },
});

const UserSelect = ({
  options = [],
  value = null,
  onChange,
  onInputChange,
  isMulti = true,
  isDisabled = false,
  isLoading = false,
  isCreatable = true,
  isBordered,
  ...props
}: IUserSelect) => {
  const ControlStyle = base => ({
    backgroundColor: isBordered ? '1px solid red' : 'transparent',
    ...base,
    border: isBordered ? '1px solid red' : 'none',
    boxShadow: 'none',
    '&:hover': {
      border: 'none',
    },
    '.react-select__value-container': {
      overflow: 'auto',
      flexWrap: 'nowrap',
    },
  });

  const handleInputChange = (inputValue: string) => {
    const normalizedInputValue = normalizeRecipientInput(inputValue);

    onInputChange?.(normalizedInputValue);

    return normalizedInputValue;
  };

  return isCreatable ? (
    <StyledCreatableSelect
      isMulti={isMulti}
      isDisabled={isDisabled}
      isClearable={false}
      placeholder="Email"
      isLoading={isLoading}
      noOptionsMessage={() => 'Введите email или имя'}
      classNamePrefix="react-select"
      filterOption={() => true}
      options={options}
      value={value}
      onChange={newValue => {
        onChange?.(newValue as ValueTypeType[] | null);
      }}
      onInputChange={handleInputChange}
      formatCreateLabel={userInput => `Cоздать ${userInput}`}
      isValidNewOption={isValidNewEmailOption}
      styles={{
        container: ContainerStyle,
        menu: MenuStyle,
        menuList: MenuListStyle,
        option: OptionStyle,
        control: ControlStyle,
        multiValue: MultiValueStyle,
        singleValue: SingleValueStyle,
        multiValueLabel: MultiValueLabelStyle,
        multiValueRemove: MultiValueRemoveStyle,
      }}
      components={{
        MenuList,
        Option,
        LoadingMessage,
      }}
      {...props}
    />
  ) : (
    <StyledSelect
      isMulti={isMulti}
      isDisabled={isDisabled}
      isClearable={false}
      placeholder="Email"
      noOptionsMessage={() =>
        'Список email-ов недоступен, обратитесь в поддержку'
      }
      isLoading={isLoading}
      classNamePrefix="react-select"
      options={options}
      value={value}
      onChange={newValue => {
        onChange?.(newValue as ValueTypeType[] | null);
      }}
      onInputChange={onInputChange}
      styles={{
        container: ContainerStyle,
        menu: MenuStyle,
        menuList: MenuListStyle,
        option: OptionStyle,
        control: ControlStyle,
        multiValue: MultiValueStyle,
        singleValue: SingleValueStyle,
        multiValueLabel: MultiValueLabelStyle,
        multiValueRemove: MultiValueRemoveStyle,
      }}
      components={{
        MenuList,
        Option,
        LoadingMessage,
      }}
      {...props}
    />
  );
};

UserSelect.defaultProps = {
  value: null,
  options: [],
  onChange: Function.prototype,
  isMulti: true,
  isDisabled: false,
  isLoading: false,
  isCreatable: true,
};

const StyledCreatableSelect = styled(SelectCreatable)`
  border: none;
  outline: none;
  display: block;
  padding: 0;
  width: 100%;
`;

const StyledSelect = styled(Select)`
  border: none;
  outline: none;
  display: block;
  padding: 0;
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledOptionAvatar = styled.img`
  float: left;
  margin-right: 10px;
  width: 26px;
  height: 26px;
  border-radius: 100%;
`;

const StyledOptionName = styled.p`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  margin-right: 5px;

  &::after {
    content: ' ';
    width: 3px;
    height: 3px;
    margin-top: 4px;
    margin-left: 5px;
    background-color: #17181a;
    border-radius: 100%;
  }
`;

const StyledOptionDescription = styled.p`
  margin-top: 1px;
  font-size: 12px;
`;

const StyledOptionEmail = styled.p`
  font-size: 12px;
`;

const StyledLoadingContainer = styled.div`
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledSpinner = styled(Throbber)`
  height: 20px;
  width: 20px;
  position: relative;
  margin: 0;
  left: 0;
  display: inline-block;

  ::before {
    width: 20px;
    height: 20px;
    bottom: 0;
    top: -1px;
    position: absolute;
  }
`;

export default memo(UserSelect);
